import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import AdminShell from "../../components/AdminShell";

export default function AdminAssignments() {
  const [admins, setAdmins] = useState([]);
  const [hirings, setHirings] = useState([]);
  const [assignments, setAssignments] = useState({}); // { adminUid: [jobId, ...] }
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [adminSnap, hiringSnap, assignSnap] = await Promise.all([
          getDocs(collection(db, "admins")),
          getDocs(collection(db, "hirings")),
          getDocs(collection(db, "admin_assignments")),
        ]);
        setAdmins(adminSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setHirings(hiringSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        const map = {};
        assignSnap.docs.forEach((d) => { map[d.id] = d.data().assignedJobs || []; });
        setAssignments(map);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const toggle = (adminId, jobId) => {
    const current = assignments[adminId] || [];
    const updated = current.includes(jobId) ? current.filter((j) => j !== jobId) : [...current, jobId];
    setAssignments({ ...assignments, [adminId]: updated });
  };

  const save = async (adminId) => {
    setSaving(adminId);
    try {
      await setDoc(doc(db, "admin_assignments", adminId), {
        assignedJobs: assignments[adminId] || [],
        updatedAt: serverTimestamp()
      });
      setMsg("Saved.");
    } catch (e) { setMsg("Error: " + e.message); }
    setSaving(null);
  };

  const clearAll = (adminId) => setAssignments({ ...assignments, [adminId]: [] });

  return (
    <AdminShell title="Job Assignments">
      <div className="alert alert-info mb-24">
        <i className="ti ti-info-circle"></i> <strong>One-time setup.</strong> Assign hirings to specific admins. Once saved, every new applicant who selects that job will be <strong>automatically routed</strong> to the assigned admin — no manual action needed per applicant. Admins with no assignments see all applicants.
      </div>
      {msg && <div className="alert alert-info">{msg}</div>}

      {admins.filter((a) => a.role !== "superadmin").length === 0 ? (
        <div className="card">
          <div className="empty-state"><i className="ti ti-user-off"></i><div>No non-superadmin admins found. Create admins first.</div></div>
        </div>
      ) : (
        admins.filter((a) => a.role !== "superadmin").map((admin) => (
          <div key={admin.id} className="card mb-16">
            <div className="flex-between mb-12">
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{admin.name || "Unnamed Admin"}</div>
                <div style={{ fontSize: 12, color: "var(--ink-400)" }}>{admin.email} · <span className="badge badge-pending">{admin.role}</span></div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => clearAll(admin.id)}>Clear All</button>
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => save(admin.id)} disabled={saving === admin.id}>
                  {saving === admin.id ? <span className="spinner"></span> : <><i className="ti ti-check"></i> Save</>}
                </button>
              </div>
            </div>

            {hirings.length === 0 ? (
              <p className="text-muted" style={{ fontSize: 13 }}>No hirings available. Add hirings first.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
                {hirings.map((h) => {
                  const checked = (assignments[admin.id] || []).includes(h.id);
                  return (
                    <label key={h.id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                      border: `1px solid ${checked ? "var(--gold-500)" : "var(--ink-200)"}`,
                      borderRadius: 8, background: checked ? "var(--gold-50)" : "white", cursor: "pointer"
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(admin.id, h.id)} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{h.flag} {h.title}</div>
                        <div style={{ fontSize: 11, color: "var(--ink-400)" }}>{h.country}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {(assignments[admin.id] || []).length === 0 && (
              <p className="text-muted mt-8" style={{ fontSize: 12 }}>
                <i className="ti ti-alert-circle"></i> No jobs assigned — this admin will see ALL applicants.
              </p>
            )}
          </div>
        ))
      )}
    </AdminShell>
  );
}
